import type { EolStyle } from "../doc/eol.js";

export function rowSeparator(eol: EolStyle): string {
  if (eol === "crlf") return "\r\n";
  if (eol === "cr") return "\r";
  return "\n";
}

/**
 * Parse comma-separated CSV with RFC 4180-style quoted fields (`""` → `"`).
 */
export function parseCsv(input: string): string[][] {
  const result: string[][] = [];
  let row: string[] = [];
  let field = "";
  let i = 0;
  let inQuotes = false;
  const len = input.length;

  const pushField = (): void => {
    row.push(field);
    field = "";
  };

  const pushRow = (): void => {
    pushField();
    result.push(row);
    row = [];
  };

  while (i < len) {
    const c = input[i]!;
    if (inQuotes) {
      if (c === '"') {
        if (i + 1 < len && input[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += c;
      i++;
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (c === ",") {
      pushField();
      i++;
      continue;
    }
    if (c === "\r") {
      if (i + 1 < len && input[i + 1] === "\n") i++;
      pushRow();
      i++;
      continue;
    }
    if (c === "\n") {
      pushRow();
      i++;
      continue;
    }
    field += c;
    i++;
  }
  pushField();
  result.push(row);
  return result.length ? result : [[""]];
}

function escapeField(s: string): string {
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function serializeCsv(rows: string[][], eol: EolStyle): string {
  const sep = rowSeparator(eol);
  return rows.map((r) => r.map(escapeField).join(",")).join(sep);
}

/** Pad rows to a common width (minimum 1 column). */
export function normalizeRows(rows: string[][]): string[][] {
  if (!rows.length) return [[""]];
  const w = Math.max(1, ...rows.map((r) => r.length));
  return rows.map((r) => {
    const copy = [...r];
    while (copy.length < w) copy.push("");
    return copy;
  });
}
